// Utils
import { isEmpty, uniq } from "lodash";
import { getElementBBox } from "utils";

// Types
import {
    buttonArgs,
    TElementConfig,
    ChartStatus,
    svgConfigAndExport,
    convertedDataBB,
    legendItem,
} from "./BehaviouralBreakdown.types";
import theme from "assets/styles/themes";
import { TCustomiseLabelsOptions } from "../components/CustomiseLabels";

export const dotRadius = 4;
export const rowHeight = 40;
export const buttonHeight = 12;
export const maxValue = 100;
export const gapBetweenBehaviours = 10;
export const chartVerticalSpace = 110;
export const labelChartSeparation = 5;
export const transitionSpeed = 400;
export const legendSpace = 20;
export const hubAverageColor = "#FF0000";
export const hubAverageStrokeDasharray = "2.08 2.08";
export const legendDotRadius = 5;
export const legendVerticalSeparationSpace = 80;
export const legendSubItemRowHeight = 26;
export const disabledColor = "#cccccc";
export const legenItemVerticalSpace = 18;

const labelDefaultConfig = {
    fill: "transparent",
    "stroke-width": 0,
};
const labelDefaultCSS = {
    whiteSpace: "break-spaces",
    fontFamily: "Lato",
};

export const convertToScale = (barElement: ReturnType<typeof getElementBBox> | undefined, value: number | null) => {
    if (!barElement) return;

    const scaleWidth = barElement.width;
    const xPosition = barElement.x;

    return (scaleWidth * (value || 0)) / maxValue + xPosition;
};

export const createLine = (
    el1: svgConfigAndExport,
    el2: svgConfigAndExport,
    lineConfig: Highcharts.SVGAttributes,
    status: ChartStatus,
    hasSubData = false,
    isSubData = false
) => {
    if (!el1 || !el2) return;

    const extraHeight = hasSubData ? buttonHeight : 0;
    const yPos = isSubData ? 0 : rowHeight + gapBetweenBehaviours + extraHeight;

    const element1 = status.isExporting ? el1.svgExport : el1.svg;
    const element2 = status.isExporting ? el2.svgExport : el2.svg;

    // getElementBBox() takes the position of the circle on screen, not to export
    const dot1 = getElementBBox(element1);
    const dot2 = getElementBBox(element2);

    return {
        args: [
            ["M", dot1.x, dot1.y],
            ["L", dot2.x, dot2.y + yPos],
        ],
        css: lineConfig,
    };
};

export const expandedLegendUtils = (data: convertedDataBB) => {
    const legendItems = data.svgs.legend.items;
    // Get an array of only parent items isLegendExpanded values
    const expandedArrays = data.data.filter((e) => e.userId).map((e) => e.isLegendExpanded);

    // Create a new array with the data for each serie + isExpanded from expandedArrays
    // and the height this legend should push the others when isExpanded is true
    const expandedLegendItems = legendItems.map((item, i) => {
        const isExpanded = expandedArrays[i];
        const subItemsYpos = item.subItems?.map((e) => getElementBBox(e.dot.svg).y);
        const uniqueElementsInYposition = uniq(subItemsYpos);
        const heightToPush = (uniqueElementsInYposition.length || 0) * legendSubItemRowHeight;

        return {
            ...item,
            isExpanded,
            height: heightToPush,
        };
    });

    // Select only the expanded legends
    const expandedLegends = expandedLegendItems.filter((e) => e.isExpanded);

    // Get the highest row number
    const maxRow = Math.max(...expandedLegendItems.map((e) => e.row));
    const rowsCount = maxRow >= 0 ? maxRow : 0;

    // Create an array where n = rowsCount [0,1,2,3...rowsCount]
    const rows = Array.from(Array(rowsCount + 1).keys());

    // Create a config with highest value per row and the amount to translate per row
    let totalTranslate = 0;
    let sumOfHeights = 0;
    const rowsConfig = rows.map((row) => {
        const elementsPerRow = expandedLegends.filter((el) => el.row === row - 1);
        const heightsHeightPerRow = elementsPerRow.map((r) => r.height);
        const highestHeight = Math.max(...[...heightsHeightPerRow, 0]);

        const elementsPerRow2 = expandedLegends.filter((el) => el.row === row);
        const heightsHeightPerRow2 = elementsPerRow2.map((r) => r.height);
        sumOfHeights += Math.max(...[...heightsHeightPerRow2, 0]);

        return {
            height: highestHeight,
            translateY: (totalTranslate += highestHeight),
        };
    });

    return { expandedLegendItems, expandedLegends, rowsCount, rows, sumOfHeights, rowsConfig };
};

export const createMasks = (
    dataEntries: [string, convertedDataBB][],
    entry: convertedDataBB[],
    status: ChartStatus,
    chart: Highcharts.Chart,
    renderer: Highcharts.SVGRenderer
) => {
    dataEntries.forEach((_, i) => {
        entry[i].svgs.mask.config = {
            ...entry[i].svgs.mask.config,
            args: [0, chart.plotTop, chart.plotWidth, rowHeight + buttonHeight],
        };

        if (status.isFirstRender) {
            entry[i].svgs.mask.svg = renderer.clipRect(...(entry[i].svgs.mask.config.args as number[]));
            entry[i].svgs.mask.svg.css(entry[i].svgs.mask.config?.css || {});
        }

        if (status.isRedrawing) {
            entry[i].svgs.mask.svg.element?.setAttribute("width", `${chart.plotWidth}`);
        }

        if (status.isExporting) {
            entry[i].svgs.mask.svgExport = renderer
                .clipRect(...(entry[i].svgs.mask.config.args as number[]))
                .css(entry[i].svgs.mask.config.css || {});
        }
    });
};

export const createWrapperGroups = (
    dataEntries: [string, convertedDataBB][],
    entry: convertedDataBB[],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    previousGroup: Highcharts.SVGElement | undefined
) => {
    dataEntries.forEach((_, i, entries) => {
        let translateY = 0;

        if (previousGroup) {
            const previousGroupTranslateY = previousGroup ? getElementBBox(previousGroup).f : 0;
            const subDataHeight =
                entries[i - 1] && entries[i - 1][1].subData?.data?.length ? rowHeight : rowHeight - buttonHeight;

            translateY =
                previousGroupTranslateY +
                subDataHeight +
                gapBetweenBehaviours * (entries[i - 1] && entries[i - 1][1].isLastGraph ? 4 : 2);
        }

        entry[i].svgs.wrapperGroup.config = {
            ...entry[i].svgs.wrapperGroup.config,
            args: [`wrapperGroup-${i}`],
            attr: {
                zIndex: entries.length - i,
            },
            translate: [0, translateY],
        };

        if (status.isFirstRender) {
            entry[i].svgs.wrapperGroup.svg = renderer
                .g(...(entry[i].svgs.wrapperGroup.config.args as string[]))
                .translate(...(entry[i].svgs.wrapperGroup.config.translate as [number, number]))
                .attr(entry[i].svgs.wrapperGroup.config.attr as Highcharts.SVGAttributes)
                .css(entry[i].svgs.wrapperGroup.config.css || {})
                .add();
        }

        if (status.isRedrawing && !isEmpty(entry[i].svgs.wrapperGroup.svg)) {
            entry[i].svgs.wrapperGroup.svg.translate(
                entry[i].svgs.wrapperGroup.config.translate?.[0] || 0,
                entry[i].svgs.wrapperGroup.config.translate?.[1] || 0
            );
        }

        if (status.isExporting) {
            entry[i].svgs.wrapperGroup.svgExport = renderer
                .g(...(entry[i].svgs.wrapperGroup.config.args as string[]))
                .css(entry[i].svgs.wrapperGroup.config.css || {})
                .translate(...(entry[i].svgs.wrapperGroup.config.translate as [number, number]))
                .attr(entry[i].svgs.wrapperGroup.config.attr as Highcharts.SVGAttributes)
                .add();
        }

        entry[i].svgs.group.config = {
            args: [`group-${i}`],
        };

        if (status.isFirstRender) {
            entry[i].svgs.group.svg = renderer
                .g(...(entry[i].svgs.group.config.args as string[]))
                .add(entry[i].svgs.wrapperGroup.svg);
        }

        if (status.isExporting) {
            entry[i].svgs.group.svgExport = renderer
                .g(...(entry[i].svgs.group.config.args as string[]))
                .add(entry[i].svgs.wrapperGroup.svgExport);
        }

        previousGroup = status.isExporting ? entry[i].svgs.wrapperGroup.svgExport : entry[i].svgs.wrapperGroup.svg;
    });
};

export const createSubBehaviourBackground = (
    entry: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    hideExpandButtons: boolean
) => {
    const height = hideExpandButtons ? rowHeight : rowHeight * (entry.subData?.data?.length || 1);

    entry.svgs.subBehaviourBar.config = {
        ...entry.svgs.subBehaviourBar.config,
        args: [
            chartVerticalSpace, // x
            chart.plotTop, // y
            chartMaxWidth,
            height,
        ],
        attr: { fill: entry.styles.bgColor },
    };

    if (status.isFirstRender) {
        entry.svgs.subBehaviourBar.svg = renderer
            .rect(...(entry.svgs.subBehaviourBar.config.args as number[]))
            .attr(entry.svgs.subBehaviourBar.config.attr)
            .css(entry.svgs.subBehaviourBar.config.css || {})
            .add(entry.svgs.group.svg);
    }

    if (status.isRedrawing) {
        entry.svgs.subBehaviourBar.svg.element?.setAttribute("width", `${chartMaxWidth}`);
    }

    if (status.isExporting) {
        entry.svgs.subBehaviourBar.svgExport = renderer
            .rect(...(entry.svgs.subBehaviourBar.config.args as number[]))
            .attr(entry.svgs.subBehaviourBar.config.attr)
            .add(entry.svgs.group.svgExport);
    }
};

export const createBehaviourBar = (
    entry: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number
) => {
    entry.svgs.bar.config = {
        ...entry.svgs.bar.config,
        args: [
            chartVerticalSpace, //x
            chart.plotTop, //y
            chartMaxWidth,
            40,
        ],
        attr: { fill: entry.styles.bgColor },
    };

    if (status.isFirstRender) {
        entry.svgs.bar.svg = renderer
            .rect(...(entry.svgs.bar.config.args as number[]))
            .attr(entry.svgs.bar.config.attr)
            .css(entry.svgs.bar.config.css || {})
            .add(entry.svgs.group.svg);
    }

    if (status.isRedrawing) {
        entry.svgs.bar.svg.element?.setAttribute("width", `${chartMaxWidth}`);
    }

    if (status.isExporting) {
        entry.svgs.bar.svgExport = renderer
            .rect(...(entry.svgs.bar.config.args as number[]))
            .attr(entry.svgs.bar.config.attr)
            .add(entry.svgs.group.svgExport);
    }
};

export const createExpanderButton = (
    entry: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    showDefinitions: boolean,
    onClickBtn: (i: number, chart: Highcharts.Chart, transitionSpeed: number) => void
) => {
    const pointerEvents = showDefinitions ? "none" : "auto";
    const btnState = {
        width: chartMaxWidth,
        height: buttonHeight,
        padding: 0,
        fill: entry.styles.buttonColor,
        "stroke-width": 0,
        zIndex: 1,
    };

    entry.svgs.button.config = {
        args: [
            "",
            chartVerticalSpace, // x
            0, // y
            onClickBtn,
            btnState,
            btnState,
            btnState,
            btnState,
        ],
        css: entry.svgs.button.config.css
            ? entry.svgs.button.config.css
            : {
                  transform: `translate(${chartVerticalSpace}px, ${rowHeight + 2}px)`,
                  pointerEvents,
              },
    };

    if (status.isFirstRender) {
        entry.svgs.button.svg = renderer
            .button(...(entry.svgs.button.config.args as buttonArgs))
            .css((entry.svgs.button.config.css || {}) as Highcharts.CSSObject)
            .add(entry.svgs.group.svg);
    }

    if (status.isRedrawing) {
        const button = entry.svgs.button.svg;
        const buttonBg = button.element.querySelector("rect");

        button.css({ pointerEvents });

        if (buttonBg) {
            buttonBg.setAttribute("width", `${chartMaxWidth}`);
            button.on("mouseleave", function () {
                buttonBg.setAttribute("width", `${chartMaxWidth}`);
            });
        }
    }

    if (status.isExporting) {
        renderer
            .button(...(entry.svgs.button.config.args as buttonArgs))
            .css((entry.svgs.button.config.css || {}) as Highcharts.CSSObject)
            .add(entry.svgs.group.svgExport);
    }

    Array.from(Array(2)).forEach((_, idx) => {
        if (isEmpty(entry.svgs.buttonChevrons?.[idx]?.config)) {
            entry.svgs.buttonChevrons[idx] = {
                config: {} as TElementConfig,
                svg: {} as Highcharts.SVGElement,
                svgExport: {} as Highcharts.SVGElement,
            };
        }

        const translateX = idx > 0 ? chartMaxWidth + chartVerticalSpace - 30 : 15 + chartVerticalSpace;
        const translateY = isEmpty(entry.svgs.buttonChevrons[idx].config.css)
            ? rowHeight + 2
            : entry.svgs.buttonChevrons[idx].config.css["transform"].split(" ")[1].replace("px)", "");

        const rotate = entry.svgs.buttonChevrons[idx].config?.css?.["transform"]?.split(" ")?.[2];

        entry.svgs.buttonChevrons[idx].config = {
            ...entry.svgs.buttonChevrons[idx].config,
            args: ["M", 1.0293, 1.70654, "L", 7.0293, 7.70654, "L", 13.0293, 1.70654],
            attr: {
                stroke: "#FDFEFF",
                "stroke-width": 1.5,
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                zIndex: 2,
            },
            css: {
                transform: `translate(${translateX}px, ${translateY}px) ${rotate || ""}`,
                pointerEvents,
            },
        };

        if (status.isFirstRender) {
            entry.svgs.buttonChevrons[idx].svg = renderer
                .path(entry.svgs.buttonChevrons[idx].config.args as Highcharts.SVGPathArray)
                .attr(entry.svgs.buttonChevrons[idx].config.attr)
                .css(entry.svgs.buttonChevrons[idx].config.css as Highcharts.CSSObject)
                .add(entry.svgs.group.svg);
        }

        if (status.isRedrawing) {
            entry.svgs.buttonChevrons[idx]?.svg?.css(entry.svgs.buttonChevrons[idx].config.css as Highcharts.CSSObject);
        }

        if (status.isExporting) {
            entry.svgs.buttonChevrons[idx].svgExport = renderer
                .path(entry.svgs.buttonChevrons[idx].config.args as Highcharts.SVGPathArray)
                .attr(entry.svgs.buttonChevrons[idx].config.attr)
                .css(entry.svgs.buttonChevrons[idx].config.css as Highcharts.CSSObject)
                .add(entry.svgs.group.svgExport);
        }
    });
};

export const createPlotLines = (
    entry: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    group: Highcharts.SVGElement,
    hideExpandButtons: boolean
) => {
    entry.svgs.plotLines.forEach((el, i) => {
        const config = {
            args: [
                (chartMaxWidth / 10) * ++i + chartVerticalSpace,
                0,
                1,
                (entry.subData?.data.length || 1) * rowHeight,
            ],
            attr: { fill: "white" },
        };

        el.config = config;

        if (status.isFirstRender) {
            el.svg = renderer
                .rect(...(config.args as number[]))
                .attr(config.attr)
                .add(group);
        }

        if (status.isRedrawing) {
            el.svg?.attr({
                x: config.args[0],
                height: config.args[3],
            });
        }

        if (status.isExporting) {
            el.svgExport = renderer
                .rect(...(config.args as number[]))
                .attr(config.attr)
                .add(group);
        }
    });

    if (!hideExpandButtons) {
        entry.subData?.data &&
            entry.subData.data.forEach((_, i) => {
                const config = {
                    args: [
                        chartVerticalSpace, // x
                        chart.plotTop + (i + 1) * rowHeight, // y
                        chartMaxWidth,
                        1,
                    ],
                    attr: { fill: "white" },
                };

                entry.svgs.subData.plotLines[i].config = config;

                if (status.isFirstRender) {
                    entry.svgs.subData.plotLines[i].svg = renderer
                        .rect(...(config.args as number[]))
                        .attr(config.attr)
                        .add(group);
                }

                if (status.isRedrawing) {
                    entry.svgs.subData.plotLines[i].svg?.attr({
                        x: config.args[0],
                        y: config.args[1],
                        width: config.args[2],
                    });
                }

                if (status.isExporting) {
                    entry.svgs.subData.plotLines[i].svgExport = renderer
                        .rect(...(config.args as number[]))
                        .attr(config.attr)
                        .add(group);
                }
            });
    }
};

export const createLabels = (
    entry: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    chartMaxWidth: number,
    shouldShowDefinitions: boolean,
    barElement: Record<string, number>,
    onClickLabel: (label: string, parentLabel: string) => void
) => {
    entry.labels.forEach((label, idx) => {
        const isExpanded = entry.expanded;

        const labelConfig = {
            args: [
                label,
                idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : barElement.x - 20,
                0,
                () => onClickLabel(label, entry.labels[0]),
                labelDefaultConfig,
                labelDefaultConfig,
                labelDefaultConfig,
            ],
            css: entry.svgs.labels?.[idx]?.config.css
                ? entry.svgs.labels[idx].config.css
                : {
                      ...labelDefaultCSS,
                      opacity: !isExpanded ? 1 : 0,
                      fontSize: "12px",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      width: 93,
                      ...(idx === 0 ? { textAnchor: "end" } : {}),
                  },
        };

        // View Definitions - Change labels styles to look like links
        if (shouldShowDefinitions) {
            entry.svgs.labels?.[idx]?.svg.addClass("chart-carousel-axis-label--active");
        } else {
            entry.svgs.labels?.[idx]?.svg.removeClass("chart-carousel-axis-label--active");
        }

        if (status.isFirstRender) {
            const labelSvg = renderer
                // @ts-ignore
                .button(...labelConfig.args)
                .css(labelConfig.css as Highcharts.CSSObject)
                .addClass("chart-carousel-axis-label")
                .add(entry.svgs.group.svg);

            labelSvg.attr({
                y: (barElement.height + 0) / 2 - labelSvg.getBBox().height / 2,
            });

            entry.svgs.labels[idx] = { config: labelConfig, svg: labelSvg };
        }

        if (status.isRedrawing) {
            entry.svgs.labels[idx].svg?.attr({
                x: idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : barElement.x - 20,
            });
        }

        if (status.isExporting) {
            renderer
                // @ts-ignore
                .button(...labelConfig.args)
                .css(labelConfig.css as Highcharts.CSSObject)
                .add(entry.svgs.group.svgExport)
                .attr({
                    x: idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : barElement.x - 20,
                });
        }

        // ROTATED LABELS
        if (entry.subData.data.length) {
            const bars =
                barElement.height * entry.subData.data.length +
                //@ts-ignore
                (entry.svgs.button.svg.element?.getBBox()?.height || 0);

            const rotatedLabelConfig = {
                args: [label, 0, 0],
                css: entry.svgs.rotatedLabels[idx]?.config.css
                    ? entry.svgs.rotatedLabels[idx].config.css
                    : {
                          ...labelDefaultCSS,
                          opacity: 0,
                          color: entry.styles.rotatedLabelsColor,
                          fontSize: "15px",
                          textTransform: "uppercase",
                      },
            };

            if (status.isFirstRender) {
                const rotatedLabelSvg = renderer
                    // @ts-ignore
                    .label(...rotatedLabelConfig.args)
                    .css(rotatedLabelConfig.css as Highcharts.CSSObject)
                    .add(entry.svgs.group.svg);

                const translate = {
                    x: idx > 0 ? chart.plotWidth - rotatedLabelSvg.getBBox().height - 10 : 0,
                    y: bars / 2 + rotatedLabelSvg.getBBox().width / 2,
                };

                rotatedLabelSvg.css({
                    transform: `translate(${translate.x}px, ${translate.y}px) rotate(-90deg)`,
                });

                entry.svgs.rotatedLabels[idx] = { config: rotatedLabelConfig, svg: rotatedLabelSvg };
            }

            if (status.isRedrawing) {
                const svgBBox = entry.svgs.rotatedLabels[idx].svg?.getBBox();
                const translate = {
                    x: idx > 0 ? chart.plotWidth - svgBBox.height : 0,
                    y: bars / 2 + svgBBox.width / 2,
                };

                entry.svgs.rotatedLabels[idx].svg?.css({
                    transform: `translate(${translate.x}px, ${translate.y}px) rotate(-90deg)`,
                });
            }

            if (status.isExporting) {
                const svgBBox = entry.svgs.rotatedLabels[idx].svg?.getBBox();
                const translate = {
                    x: idx > 0 ? chart.plotWidth - svgBBox.height : 0,
                    y: bars / 2 + svgBBox.width / 2,
                };

                renderer
                    // @ts-ignore
                    .label(...rotatedLabelConfig.args)
                    .css({
                        ...rotatedLabelConfig.css,
                        transform: `translate(${translate.x}px, ${translate.y}px) rotate(-90deg)`,
                    } as Highcharts.CSSObject)
                    .add(entry.svgs.group.svgExport);
            }
        }
    });
};

export const createSubLabels = (
    entry: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chartMaxWidth: number,
    shouldShowDefinitions: boolean,
    onClickLabel: (label: string, parentLabel: string) => void
) => {
    entry.subData.labels?.length &&
        entry.subData.labels.forEach((label, labelIdx) => {
            entry.svgs.subData.labels.push([]);
            label.forEach((text, idx) => {
                const isExpanded = entry.expanded;

                const xPosition =
                    idx > 0 ? chartMaxWidth + chartVerticalSpace + labelChartSeparation : 82 + labelChartSeparation;

                const config = {
                    args: [
                        text,
                        xPosition,
                        0,
                        () => onClickLabel(text, entry.labels[0]),
                        labelDefaultConfig,
                        labelDefaultConfig,
                        labelDefaultConfig,
                    ],
                    attr: {},
                    css: entry.svgs.subData.labels?.[labelIdx]?.[idx]?.config.css
                        ? entry.svgs.subData.labels[labelIdx][idx].config.css
                        : {
                              ...labelDefaultCSS,
                              opacity: isExpanded ? 1 : 0,
                              fontSize: "10px",
                              width: 50,
                              color: theme.palette.text.primary,
                              ...(idx === 0 ? { textAnchor: "end" } : {}),
                          },
                };

                // View Definitions - Change labels styles to look like links
                if (shouldShowDefinitions) {
                    entry.svgs.subData.labels[labelIdx]?.[idx]?.svg.addClass("chart-carousel-axis-label--active");
                } else {
                    entry.svgs.subData.labels[labelIdx]?.[idx]?.svg.removeClass("chart-carousel-axis-label--active");
                }

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .button(...config.args)
                        .css(config.css as Highcharts.CSSObject)
                        .addClass("chart-carousel-axis-label")
                        .add(entry.svgs.group.svg);

                    entry.svgs.subData.labels[labelIdx].push({ config, svg });
                }

                if (status.isRedrawing) {
                    const svg = entry.svgs.subData.labels[labelIdx][idx]?.svg;
                    const coors = {
                        x: xPosition,
                        y: rowHeight * (labelIdx + 1) - rowHeight / 2 - svg.element.getBoundingClientRect().height / 2,
                    };
                    svg.attr(coors);
                }

                if (status.isExporting) {
                    const svg = entry.svgs.subData.labels[labelIdx][idx]?.svg;
                    const coors = {
                        x: xPosition,
                        y: rowHeight * (labelIdx + 1) - rowHeight / 2 - svg.element.getBoundingClientRect().height / 2,
                    };
                    renderer
                        // @ts-ignore
                        .button(...config.args)
                        .css(config.css as Highcharts.CSSObject)
                        .addClass("chart-carousel-axis-label")
                        .add(entry.svgs.group.svgExport)
                        .attr(coors);
                }
            });
        });
};

export const createSummaryDot = (
    dataEntries: [string, convertedDataBB][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    barElement: ReturnType<typeof getElementBBox> | undefined
) => {
    dataEntries.forEach((el) => {
        el[1].data.forEach((element, idx) => {
            const isHubAverage = el[1].data[idx].label === "Hub Average";
            const isVisible = element.visible;
            const isEnabled = element.enabled;
            const isExpanded = el[1].expanded;

            const isNotExpandedAndVisible = !isExpanded && isVisible;

            const baseColor = isHubAverage ? hubAverageColor : element.color;
            const fillColor = isNotExpandedAndVisible ? baseColor : "transparent";

            const config = {
                args: [convertToScale(barElement, element.value), 20, dotRadius],
                attr: {
                    id: `main-${idx}--${element.label}-${element.value}`,
                    zIndex: 2,
                },
                css: el[1].svgs.dots[idx]?.config.css
                    ? el[1].svgs.dots[idx]?.config.css
                    : { fill: !isEnabled || !isVisible ? "transparent" : fillColor },
                // : { fill: !isEnabled ? "transparent" : fillColor, opacity: !isEnabled ? 0 : opacity },
            };

            if (status.isFirstRender) {
                const svg = renderer
                    .circle(...(config.args as unknown as Highcharts.SVGAttributes[]))
                    .attr(config.attr)
                    .css(config.css)
                    .add(el[1].svgs.group.svg);

                el[1].svgs.dots[idx] = { ...el[1].svgs.dots[idx], config, svg };
            }

            if (status.isRedrawing) {
                el[1].svgs.dots[idx].svg?.attr({
                    x: convertToScale(barElement, element.value),
                });
            }

            if (status.isExporting) {
                el[1].svgs.dots[idx].svgExport = renderer
                    .circle(...(config.args as unknown as Highcharts.SVGAttributes[]))
                    .attr({ ...config.attr, x: convertToScale(barElement, element.value) })
                    .css(config.css)
                    .add(el[1].svgs.group.svgExport);
            }
        });
    });
};

export const createSummaryDotLabel = (
    dataEntries: [string, convertedDataBB][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    customiseLabelsOptions: TCustomiseLabelsOptions
) => {
    dataEntries.forEach((el, i) => {
        if (i === 0 || i === 4) {
            el[1].data.forEach((element, idx) => {
                const isVisible = element.visible;
                const isEnabled = element.enabled;
                const isExpanded = el[1].expanded;

                const showNames = customiseLabelsOptions["show-names"];

                const labelText = isVisible && !isExpanded && showNames && isEnabled ? element.label : "";
                const dot = status.isExporting ? el[1].svgs.dots[idx].svgExport : el[1].svgs.dots[idx].svg;

                const config = {
                    args: [labelText, getElementBBox(dot).x, getElementBBox(dot).y - 20],
                    css: {
                        fontFamily: "Lato",
                        fontSize: "12px",
                        textAnchor: (el[1].data[idx].value || 0) >= 75 ? "end" : "start",
                        color: "#454852",
                    },
                    attr: {
                        zIndex: 2,
                    },
                };

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .label(...(config.args as Highcharts.SVGAttributes[]))
                        .css(config.css as Highcharts.CSSObject)
                        .attr(config.attr)
                        .add(el[1].svgs.group.svg);

                    el[1].svgs.dotsLabels[idx] = { config, svg };
                }

                if (status.isRedrawing) {
                    el[1].svgs.dotsLabels[idx].svg
                        .attr({
                            text: labelText,
                            x: getElementBBox(dot).x,
                        })
                        .css(config.css as Highcharts.CSSObject);
                }

                if (status.isExporting) {
                    const labelConfig = [labelText, getElementBBox(dot).x, getElementBBox(dot).y - 20];
                    renderer
                        // @ts-ignore
                        .label(...labelConfig)
                        .css(config.css as Highcharts.CSSObject)
                        .add(el[1].svgs.group.svgExport);
                }
            });
        }
    });
};

export const createDotLineConnector = (
    dataEntries: [string, convertedDataBB][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer
) => {
    dataEntries.forEach((entry, entryIdx) => {
        if (!entry[1].isLastGraph) {
            const data = dataEntries[entryIdx][1];

            entry[1].svgs.dots.forEach((dot, dotIdx) => {
                const isExpanded = entry[1].expanded;
                const isNextElementExpanded = dataEntries[entryIdx + 1][1].expanded;
                const isVisible = entry[1].data[dotIdx].visible;

                const isEnabled = entry[1].data[dotIdx].enabled;
                const isHubAverage = entry[1].data[dotIdx].label === "Hub Average";

                const strokeWidth = entry[1].svgs?.lines?.[dotIdx]?.config?.css
                    ? entry[1].svgs.lines[dotIdx].config.css?.["stroke-width"]
                    : 1;

                const config = createLine(
                    dot,
                    dataEntries[entryIdx + 1][1].svgs.dots[dotIdx],
                    {
                        "stroke-width":
                            !isEnabled || !isVisible || isExpanded || isNextElementExpanded ? 0 : strokeWidth,
                        stroke: isHubAverage ? hubAverageColor : entry[1].data[dotIdx].color,
                        ...(isHubAverage ? { "stroke-dasharray": hubAverageStrokeDasharray } : {}),
                    },
                    status,
                    Boolean(entry[1].subData.data.length)
                );

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .path(config.args)
                        // @ts-ignore
                        // .attr(config.attr)
                        // @ts-ignore
                        .css(config.css || {})
                        .add(data.svgs.wrapperGroup.svg);

                    entry[1].svgs.lines.push({ svg, ...{ config: config || {} } });
                }

                if (status.isRedrawing) {
                    if (config) {
                        entry[1].svgs.lines[dotIdx].svg
                            ?.attr({
                                d: config.args as any,
                            })
                            .css(config.css || {});
                    }
                }

                if (status.isExporting) {
                    renderer
                        // @ts-ignore
                        .path(config.args)
                        // @ts-ignore
                        //.attr(config.attr)
                        // @ts-ignore
                        .css(config.css || {})
                        .add(data.svgs.wrapperGroup.svgExport);
                }
            });
        }
    });
};

export const createSubBehaviourDots = (
    dataEntries: [string, convertedDataBB][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    barElement: ReturnType<typeof getElementBBox> | undefined,
    customiseLabelsOptions: TCustomiseLabelsOptions
) => {
    dataEntries.forEach((el, i) => {
        el[1].subData?.data?.forEach((arrayOfDots, arrayOfDotsIdx) => {
            if (status.isFirstRender) {
                el[1].svgs.subData.dots.push([]);
            }

            arrayOfDots.forEach((dot, idx) => {
                const isVisible = el[1].data[idx].visible ? 1 : 0;
                const isEnabled = el[1].data[idx].enabled;
                const isExpanded = el[1].expanded;

                const isHubAverage = el[1].data[idx].label === "Hub Average";
                const fillColor = isHubAverage ? hubAverageColor : el[1].data[idx].color;
                const fillOpacity = isVisible && isExpanded ? 1 : 0;

                const config = {
                    args: [
                        convertToScale(barElement, dot || 0),
                        (barElement?.height || 1) / 2 + arrayOfDotsIdx * 40,
                        dotRadius,
                    ],
                    attr: {
                        id: `sub-${idx}--${dot}`,
                        zIndex: 2,
                    },
                    css: {
                        ...el[1].svgs.dots[idx]?.config.css,
                        fill: !isEnabled ? "transparent" : fillColor,
                        opacity: !isEnabled ? 0 : fillOpacity,
                    },
                };

                if (status.isFirstRender) {
                    const svg = renderer
                        // @ts-ignore
                        .circle(...config.args)
                        .attr(config.attr)
                        // @ts-ignore
                        .css(config.css || {})
                        .add(el[1].svgs.group.svg);

                    el[1].svgs.subData.dots[arrayOfDotsIdx].push({
                        config,
                        svg,
                        svgExport: {} as Highcharts.SVGElement,
                    });
                }

                if (status.isRedrawing) {
                    el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svg
                        ?.attr({
                            ...(config?.attr ? (config.attr as Highcharts.SVGAttributes) : {}),
                            x: convertToScale(barElement, dot || 0),
                        })
                        .css(config.css || {});
                }

                if (status.isExporting) {
                    el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svgExport = renderer
                        // @ts-ignore
                        .circle(...config.args)
                        .attr(config.attr)
                        .attr({ x: convertToScale(barElement, dot || 0) })
                        // @ts-ignore
                        .css(config.css || {})
                        .add(el[1].svgs.group.svgExport);
                }

                // SUB-BEHAVIOUR DOT LABELS
                if (arrayOfDotsIdx === 0) {
                    const isExpanded = el[1].expanded;
                    const isVisible = el[1].data[idx].visible ? 1 : 0;
                    const isEnabled = el[1].data[idx].enabled;

                    const showNames = customiseLabelsOptions["show-names"];
                    const labelText = isVisible && isExpanded && showNames && isEnabled ? el[1].data[idx].label : "";

                    const firstDot = status.isExporting
                        ? el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svgExport
                        : el[1].svgs.subData.dots[arrayOfDotsIdx][idx].svg;

                    const config = el[1].svgs.subData.dotsLabels?.[idx]?.config
                        ? el[1].svgs.subData.dotsLabels[idx].config
                        : {
                              args: [labelText, 0, 0],
                              css: {
                                  fontFamily: "Lato",
                                  fontSize: "12px",
                                  textAnchor: (el[1].data[idx].value || 0) >= 75 ? "end" : "start",
                              },
                              attr: {
                                  zIndex: 2,
                              },
                          };

                    if (status.isFirstRender) {
                        const svg = renderer
                            // @ts-ignore
                            .label(...config.args)
                            .css(config.css as Highcharts.CSSObject)
                            .attr(config.attr)
                            .add(el[1].svgs.group.svg);

                        svg.attr({
                            x: getElementBBox(firstDot).x,
                            y: getElementBBox(firstDot).y - 20,
                        });

                        el[1].svgs.subData.dotsLabels[idx] = { config, svg };
                    }

                    if (status.isRedrawing) {
                        el[1].svgs.subData.dotsLabels[idx].svg?.attr({
                            text: labelText,
                            x: getElementBBox(firstDot).x,
                        });
                    }

                    if (status.isExporting) {
                        const labelConfig = [labelText, getElementBBox(firstDot).x, getElementBBox(firstDot).y - 20];
                        renderer
                            // @ts-ignore
                            .label(...labelConfig)
                            .css(config.css as Highcharts.CSSObject)
                            .attr(config.attr)
                            .add(el[1].svgs.group.svgExport);
                    }
                }
            });
        });
    });
};

export const createSubDotLineConnector = (
    dataEntries: [string, convertedDataBB][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer
) => {
    dataEntries.forEach((entry, entryIdx) => {
        entry[1].svgs.subData?.dots.forEach((arrayOfDots, arrayOfDotsIdx) => {
            if (arrayOfDotsIdx + 1 < entry[1].svgs.subData.dots.length) {
                const data = dataEntries[entryIdx][1];

                if (status.isFirstRender) {
                    data.svgs.subData.lines.push([]);
                }

                arrayOfDots.forEach((dot, dotIdx) => {
                    const isVisible = entry[1].data[dotIdx].visible;
                    const isEnabled = entry[1].data[dotIdx].enabled;
                    const isExpanded = data.expanded;

                    const isHubAverage = entry[1].data[dotIdx].label === "Hub Average";
                    const strokeWidth = isVisible && isExpanded ? 1 : 0;

                    const config = createLine(
                        dot,
                        entry[1].svgs.subData.dots[arrayOfDotsIdx + 1][dotIdx],
                        {
                            "stroke-width": !isEnabled ? 0 : strokeWidth,
                            stroke: isHubAverage ? hubAverageColor : entry[1].data[dotIdx].color,
                            ...(isHubAverage ? { "stroke-dasharray": hubAverageStrokeDasharray } : {}),
                        },
                        status,
                        Boolean(entry[1].subData.data.length),
                        true
                    );

                    if (!config) return;

                    if (status.isFirstRender) {
                        const svg = renderer
                            // @ts-ignore
                            .path(config.args)
                            // @ts-ignore
                            .attr(config.attr)
                            // @ts-ignore
                            .css(config.css || {})
                            .add(data.svgs.group.svg);

                        data.svgs.subData.lines[arrayOfDotsIdx].push({ svg, config });
                    }

                    if (status.isRedrawing) {
                        data.svgs.subData.lines[arrayOfDotsIdx][dotIdx].svg
                            ?.attr({
                                d: config.args as any,
                            })
                            .css(config.css || {});
                    }

                    if (status.isExporting) {
                        renderer
                            // @ts-ignore
                            .path(config.args)
                            // @ts-ignore
                            .attr(config.attr)
                            // @ts-ignore
                            .css(config.css || {})
                            .add(data.svgs.group.svgExport);
                    }
                });
            }
        });
    });
};

export const createCustomLegend = (
    dataEntries: [string, convertedDataBB][],
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    chart: Highcharts.Chart,
    hideShowSerie: (element: convertedDataBB["data"][number]) => void,
    handleExpandLegend: (elements: convertedDataBB, index: number) => void
) => {
    const el = dataEntries[0][1];
    let row = 0;

    const chartMaxWidth = chart.plotWidth;
    const lastWrapperGroup = dataEntries.at(-1)?.[1].svgs.wrapperGroup as svgConfigAndExport;

    const data = el.data;
    const legendItems = el.data.filter((item) => !item.relatedUserId);

    // -----------
    // Wrapper
    // -----------
    const wrapperGroupConfig = {
        ...el.svgs.legend.wrapperGroup.config,
        args: [`legendWrapperGroup`],
        translate: [legendDotRadius, getElementBBox(lastWrapperGroup.svg).f + legendVerticalSeparationSpace] as [
            number,
            number
        ],
    };

    if (isEmpty(el.svgs.legend.wrapperGroup.svg)) {
        el.svgs.legend.wrapperGroup = {
            ...el.svgs.legend.wrapperGroup,
            config: wrapperGroupConfig,
        };

        if (status.isFirstRender) {
            el.svgs.legend.wrapperGroup.svg = renderer
                .g(...wrapperGroupConfig.args)
                .translate(...wrapperGroupConfig.translate)
                .css(wrapperGroupConfig?.css || {})
                .attr(wrapperGroupConfig.attr)
                .add();
        }
    }

    if (status.isRedrawing) {
        lastWrapperGroup.svg.attr(wrapperGroupConfig.attr);
    }

    if (status.isExporting) {
        const wrapperGroupConfig = el.svgs.legend.wrapperGroup.config;

        const groupSvg = renderer
            .g(...(wrapperGroupConfig.args as string[]))
            .translate(...(wrapperGroupConfig.translate as [number, number]))
            .css(wrapperGroupConfig?.css || {})
            .attr(wrapperGroupConfig.attr)
            .add();

        el.svgs.legend.wrapperGroup = {
            ...el.svgs.legend.wrapperGroup,
            svgExport: groupSvg,
        };
    }

    // -----------
    // Key
    // -----------
    const keyConfig = {
        ...el.svgs.legend.key.config,
        args: ["KEY", 0, 0],
        css: {
            ...labelDefaultCSS,
            fontSize: "12px",
            fontWeight: "semibold",
            color: "#949698",
            opacity: data.length ? 1 : 0,
        },
    };

    if (status.isFirstRender && isEmpty(el.svgs.legend.key.svg)) {
        const keyTextSvg = renderer
            .text(...(keyConfig.args as (string | undefined)[]))
            .css(keyConfig.css as Highcharts.CSSObject)
            .add(el.svgs.legend.wrapperGroup.svg);

        el.svgs.legend.key = {
            config: keyConfig,
            svg: keyTextSvg,
            svgExport: keyTextSvg,
        };
    }

    if (status.isRedrawing) {
        const keyTextSvg = el.svgs.legend.key.svg;

        keyTextSvg.css(keyConfig.css);

        el.svgs.legend.key = {
            config: keyConfig,
            svg: keyTextSvg,
            svgExport: keyTextSvg,
        };
    }

    if (status.isExporting) {
        const keyConfig = el.svgs.legend.key.config;
        renderer
            .text(...(keyConfig.args as (string | undefined)[]))
            .css(keyConfig.css as Highcharts.CSSObject)
            .add(el.svgs.legend.wrapperGroup.svgExport);
    }

    // -----------
    // Legend Items
    // -----------
    legendItems.forEach((element, i) => {
        const isHubAverage = element.label === "Hub Average";
        const isVisible = element.visible;

        const itemConfig: legendItem = {
            ...el.svgs.legend.items[i],
            id: 0,
            row,
        };

        // Position for the 1st items
        const xPosExtra = isHubAverage ? 16 : dotRadius;
        let xPos = getElementBBox(el.svgs.legend.key.svg).x + xPosExtra;
        let yPos = getElementBBox(el.svgs.legend.key.svg).y + 20;

        // Position for the rest of the items
        const prevElement = el.svgs.legend.items?.[i - 1];

        if (prevElement) {
            const prevDot = getElementBBox(prevElement.dot.svg);
            let prevLastElement = {} as ReturnType<typeof getElementBBox>;

            if (!isEmpty(prevElement?.circle?.svg)) {
                prevLastElement = getElementBBox(prevElement.circle.svg);
            } else {
                prevLastElement = getElementBBox(prevElement.label.svg);
            }

            xPos = prevLastElement?.x + prevLastElement.width + 16;
            yPos = prevDot.y;
        }

        // -----------
        // Dot
        // -----------
        const dotConfig = {
            ...el.svgs.legend.items?.[i - 1]?.dot?.config,
            ...el.svgs.legend.items[i]?.dot.config,
            args: [xPos, yPos, legendDotRadius],
            css: {
                fill: isVisible ? element.color : disabledColor,
                cursor: "pointer" as const,
            },
            attr: {
                zIndex: 20,
            },
        };

        if (status.isFirstRender) {
            const svg = renderer
                .circle(...(dotConfig.args as unknown as Highcharts.SVGAttributes[]))
                .css(dotConfig.css)
                .attr(dotConfig.attr)
                .translate(...((dotConfig?.translate as [number, number]) || [0, 0]))
                .on("click", () => hideShowSerie(element))
                .add(el.svgs.legend.wrapperGroup.svg);

            el.svgs.legend.items[i] = {
                ...itemConfig,
                id: element.id,
                row,
                dot: { config: dotConfig, svg, svgExport: svg },
                label: {
                    ...el.svgs.legend.items[i]?.label,
                },
            };
        }

        if (status.isRedrawing) {
            el.svgs.legend.items[i].dot.svg.attr(dotConfig.attr).css(dotConfig.css);
        }

        if (status.isExporting) {
            const dotSvg = getElementBBox(el.svgs.legend.items[i].dot.svg);
            const dotConfig = el.svgs.legend.items[i].dot.config;
            renderer
                .circle(...[dotSvg.x, dotSvg.y, legendDotRadius])
                .css(dotConfig.css)
                .attr(dotConfig.attr)
                .translate(dotSvg.e, dotSvg.f)
                .add(el.svgs.legend.wrapperGroup.svgExport);
        }

        // -----------
        // Average Dashed Line
        // -----------
        if (isHubAverage) {
            const dotSvg = getElementBBox(el.svgs.legend.items[i].dot.svg);
            const lineConfig = {
                ...el.svgs.legend.items[i].dashedLine?.config,
                args: [
                    ["M", 0, dotSvg.y],
                    ["L", 32, dotSvg.y],
                ],
                attr: {
                    id: "hub-average-dashed-line",
                    "stroke-width": 1,
                    stroke: isVisible ? hubAverageColor : disabledColor,
                    dashstyle: "ShortDash",
                },
            };

            if (status.isFirstRender) {
                const lineSvg = renderer
                    // @ts-expect-error expected error
                    .path(lineConfig.args)
                    .attr(lineConfig.attr)
                    .add(el.svgs.legend.wrapperGroup.svg);

                el.svgs.legend.items[i].dashedLine = {
                    config: lineConfig,
                    svg: lineSvg,
                    svgExport: lineSvg,
                };
            }

            if (status.isRedrawing) {
                el.svgs.legend.items[i].dashedLine?.svg.attr(lineConfig.attr);
            }

            if (status.isExporting) {
                // @ts-expect-error expected error
                renderer.path(lineConfig.args).attr(lineConfig.attr).add(el.svgs.legend.wrapperGroup.svgExport);
            }
        }

        // -----------
        // Label
        // -----------
        const dot = getElementBBox(el.svgs.legend.items[i].dot.svg);

        let labelX = dot.x + dot.width + 4;
        const labelY = dot.y + dot.height - 1;

        const svgElement = el?.svgs?.legend?.items[i]?.dashedLine?.svg;
        if (svgElement && !isEmpty(svgElement)) {
            const line = getElementBBox(svgElement);
            labelX = line.x + line.width + 4;
        }

        const textConfig = {
            ...el.svgs.legend.items?.[i - 1]?.label?.config,
            ...el.svgs.legend.items[i].label.config,
            args: [element.label, labelX, labelY],
            css: {
                ...labelDefaultCSS,
                fontSize: "12px",
                fontWeight: "semibold",
                color: isVisible ? "#202020" : disabledColor,
                cursor: "pointer" as const,
            },
        };

        if (status.isFirstRender) {
            const textSvg = renderer
                .text(...(textConfig.args as (string | undefined)[]))
                .css(textConfig.css as Highcharts.CSSObject)
                .translate(...((textConfig?.translate as [number, number]) || [0, 0]))
                .on("click", () => hideShowSerie(element))
                .add(el.svgs.legend.wrapperGroup.svg);

            el.svgs.legend.items[i].label = {
                config: textConfig,
                svg: textSvg,
                svgExport: textSvg,
            };
        }

        if (status.isRedrawing) {
            el.svgs.legend.items[i].label.svg
                .attr({
                    x: textConfig.args[1],
                    y: textConfig.args[2],
                })
                .css(textConfig.css);

            // el.svgs.legend.items[i].label.config = textConfig;
        }

        if (status.isExporting) {
            const textSvg = getElementBBox(el.svgs.legend.items[i].label.svg);
            renderer
                .text(...(textConfig.args as (string | undefined)[]))
                .css(textConfig.css as Highcharts.CSSObject)
                .translate(textSvg.e, textSvg.f)
                .add(el.svgs.legend.wrapperGroup.svgExport);
        }

        const legendItem = el.svgs.legend.items[i];
        const label = getElementBBox(legendItem.label.svg);
        const childrenLegend = el.data.filter((item) => item.relatedUserId === element.userId);

        // If current element is out of the chartSpace
        // Let's move it to a new line
        if (childrenLegend.length) {
            // -----------
            // Circle
            // -----------
            const circleConfig = {
                ...el.svgs.legend.items?.[i - 1]?.circle?.config,
                ...el.svgs.legend.items[i].circle?.config,
                args: [0, 0, 8],
                css: {
                    fill: "#fff",
                    stroke: isVisible ? element.color : disabledColor,
                    cursor: "pointer" as const,
                },
            };

            const text = getElementBBox(el.svgs.legend.items[i].label?.svg);

            if (status.isFirstRender) {
                const circleSvg = renderer
                    .circle(...(circleConfig.args as unknown as Highcharts.SVGAttributes[]))
                    .css(circleConfig.css)
                    .translate(...((circleConfig?.translate as [number, number]) || [0, 0]))
                    .on("click", () => handleExpandLegend(el, i))
                    .add(el.svgs.legend.wrapperGroup.svg);

                const circle = getElementBBox(circleSvg);

                circleSvg.attr({
                    x: text.x + text.width + circle.width + 4,
                    y: text.y - circle.height / 2,
                });

                el.svgs.legend.items[i].circle = {
                    config: {
                        ...circleConfig,
                        args: [getElementBBox(circleSvg).x, getElementBBox(circleSvg).y, 8],
                    },
                    svg: circleSvg,
                    svgExport: circleSvg,
                };
            }

            if (status.isRedrawing) {
                const circleSvg = el.svgs.legend.items[i].circle.svg;
                const circle = getElementBBox(circleSvg);

                circleSvg
                    .attr({
                        x: text.x + text.width + circle.width + 4,
                        y: text.y - circle.height / 2,
                    })
                    .css(el.svgs.legend.items[i].circle.config?.css);

                el.svgs.legend.items[i].circle = {
                    config: {
                        ...circleConfig,
                        args: [getElementBBox(circleSvg).x, getElementBBox(circleSvg).y, 8],
                        css: {
                            ...el.svgs.legend.items[i].circle.config?.css,
                        },
                    },
                    svg: circleSvg,
                    svgExport: circleSvg,
                };
            }

            if (status.isExporting) {
                const circleSvg = getElementBBox(el.svgs.legend.items[i].circle.svg);
                const circleConfig = el.svgs.legend.items[i].circle.config;
                renderer
                    .circle(...(circleConfig.args as unknown as Highcharts.SVGAttributes[]))
                    .css(el.svgs.legend.items[i].circle.config?.css)
                    .translate(circleSvg.e, circleSvg.f)
                    .add(el.svgs.legend.wrapperGroup.svgExport);
            }

            // -----------
            // Count
            // -----------
            const countConfig = {
                ...el.svgs.legend.items?.[i - 1]?.count?.config,
                ...el.svgs.legend.items[i].count?.config,
                args: [`+${childrenLegend.length}`, 0, 0],
                css: {
                    ...labelDefaultCSS,
                    fontSize: "8px",
                    fontWeight: "semibold",
                    color: isVisible ? "#202020" : disabledColor,
                    cursor: "pointer" as const,
                },
            };

            if (status.isFirstRender) {
                const countSvg = renderer
                    // @ts-expect-error expected error
                    .text(...countConfig.args)
                    .css(countConfig.css as Highcharts.CSSObject)
                    .translate(...((countConfig.translate as [number, number]) || [0, 0]))
                    .on("click", () => handleExpandLegend(el, i))
                    .add(el.svgs.legend.wrapperGroup.svg);

                const circle = getElementBBox(el.svgs.legend.items[i].circle.svg);
                const count = getElementBBox(countSvg);

                countSvg.attr({
                    x: circle.x - circle.width / 2 - 1,
                    y: circle.y - circle.height / 2 + count.height / 2 + 2,
                });

                el.svgs.legend.items[i].count = {
                    config: countConfig,
                    svg: countSvg,
                    svgExport: countSvg,
                };
            }

            if (status.isRedrawing) {
                const countSvg = el.svgs.legend.items[i].count.svg;

                const circle = getElementBBox(el.svgs.legend.items[i].circle.svg);
                const count = getElementBBox(countSvg);

                countSvg
                    .attr({
                        x: circle.x - circle.width / 2 - 1,
                        y: circle.y - circle.height / 2 + count.height / 2 + 2,
                    })
                    .css(countConfig.css);

                el.svgs.legend.items[i].count = {
                    config: {
                        ...countConfig,
                        args: [`+${childrenLegend.length}`, getElementBBox(countSvg).x, getElementBBox(countSvg).y],
                    },
                    svg: countSvg,
                    svgExport: countSvg,
                };
            }

            if (status.isExporting) {
                const countSvg = getElementBBox(el.svgs.legend.items[i].count.svg);
                const countConfig = el.svgs.legend.items[i].count.config;
                renderer
                    // @ts-expect-error expected error
                    .text(...countConfig.args)
                    .css(countConfig.css as Highcharts.CSSObject)
                    .translate(countSvg.e, countSvg.f)
                    .add(el.svgs.legend.wrapperGroup.svgExport);
            }

            const circle = getElementBBox(legendItem.circle.svg);

            // If current element is out of the chartSpace
            // Let's move it to a new line
            if (circle.x + circle.width > chartMaxWidth) {
                row += 1;

                el.svgs.legend.items[i].row = row;

                // Update dot
                const prevDot = getElementBBox(el.svgs.legend.items[i - 1].dot.svg);
                const yPos = prevDot.y + prevDot.height + legenItemVerticalSpace;

                el.svgs.legend.items[i].dot.svg.attr({
                    x: dotRadius,
                    y: yPos,
                });

                el.svgs.legend.items[i].dot.config = {
                    ...el.svgs.legend.items[i].dot.config,
                    args: [legendDotRadius, yPos, legendDotRadius],
                };

                // Update label
                const dot = getElementBBox(el.svgs.legend.items[i].dot.svg);
                const text = el.svgs.legend.items[i].label.svg;

                text.attr({
                    x: dot.x + dot.width + 4,
                    y: dot.y + dot.height - 1,
                });

                const textSvg = getElementBBox(text);

                el.svgs.legend.items[i].label.config = {
                    ...el.svgs.legend.items[i].label.config,
                    args: [element.label, textSvg.x, textSvg.y],
                };

                // Update circle
                const circle = el.svgs.legend.items[i].circle.svg;

                circle.attr({
                    x: textSvg.x + textSvg.width + getElementBBox(circle).width + 4,
                    y: textSvg.y - getElementBBox(circle).height / 2,
                });

                const circleSvg = getElementBBox(circle);

                el.svgs.legend.items[i].circle.config = {
                    ...el.svgs.legend.items[i].circle.config,
                    args: [circleSvg.x, circleSvg.y, 8],
                };

                // Update count
                const count = el.svgs.legend.items[i].count.svg;

                count.attr({
                    x: circleSvg.x - circleSvg.width / 2 - 1,
                    y: circleSvg.y - circleSvg.height / 2 + getElementBBox(count).height / 2 + 2,
                });

                const countSvg = getElementBBox(count);

                el.svgs.legend.items[i].count.config = {
                    ...el.svgs.legend.items[i].count.config,
                    args: [`+${childrenLegend.length}`, countSvg.x, countSvg.y],
                };
            }

            const parentConfig = el.svgs.legend.items[i];

            const parentItemDot = getElementBBox(parentConfig.dot.svg);
            const parentItemCircle = getElementBBox(parentConfig.circle.svg);

            // -----------
            // Mask
            // -----------
            const x = parentItemDot.x - parentItemDot.width - 1;
            const y = parentItemDot.y + parentItemCircle.height + 1;
            const width = parentItemCircle.x + parentItemCircle.width - x;

            const previousConfig = parentConfig.mask?.config;

            const maskConfig = {
                ...previousConfig,
                args: [x, y, width, (previousConfig?.attr as Highcharts.SVGAttributes)?.height || 0],
            };

            if (status.isFirstRender) {
                const maskSvg = renderer.clipRect(...maskConfig.args);

                parentConfig.mask = {
                    config: maskConfig,
                    svg: maskSvg,
                    svgExport: maskSvg,
                };
            }

            if (status.isRedrawing && parentConfig.mask && parentConfig.mask.svg) {
                parentConfig.mask.svg.attr({
                    x: maskConfig.args[0],
                    y: maskConfig.args[1],
                    width: maskConfig.args[2],
                    height: maskConfig.args[3],
                });
            }

            if (status.isExporting && parentConfig.mask && parentConfig.mask.svg) {
                parentConfig.mask.svgExport = renderer
                    .clipRect(...maskConfig.args)
                    .translate(...((maskConfig?.translate as [number, number]) || [0, 0]))
                    .attr(parentConfig.mask.config);
            }

            // -----------
            // Group
            // -----------
            if (status.isFirstRender && parentConfig.mask?.svg) {
                const groupConfig = {
                    ...parentConfig.group?.config,
                    args: [`legendSubitems-${element.userId}`],
                    attr: { zIndex: 2 },
                };

                const groupSvg = renderer
                    .g(...groupConfig.args)
                    .attr(groupConfig.attr)
                    // .translate(...(groupConfig?.translate || [0, 0]))
                    .clip(parentConfig.mask.svg)
                    .add(el.svgs.legend.wrapperGroup.svg);

                parentConfig.group = {
                    config: groupConfig,
                    svg: groupSvg,
                    svgExport: groupSvg,
                };

                el.svgs.legend.items[i].subItems = [] as legendItem[];
            }

            if (status.isExporting && parentConfig.mask?.svg && parentConfig.group?.svg) {
                parentConfig.group.svgExport = renderer
                    .g(`legendSubitems-${element.userId}`)
                    .attr(parentConfig.group.config.attr)
                    .add(el.svgs.legend.wrapperGroup.svgExport)
                    .clip(parentConfig.mask.svgExport);
            }

            // -----------
            //  Legend Children
            // -----------
            createCustomLegendChildren(el, status, renderer, childrenLegend, i, el.svgs.legend.items[i], hideShowSerie);
        } else {
            if (label.x + label.width > chartMaxWidth - 10) {
                row += 1;

                el.svgs.legend.items[i].row = row;

                // Update dot
                const prevDot = getElementBBox(el.svgs.legend.items[i - 1].dot.svg);
                const yPos = prevDot.y + prevDot.height + 18;

                el.svgs.legend.items[i].dot.svg.attr({
                    x: dotRadius,
                    y: yPos,
                });

                el.svgs.legend.items[i].dot.config = {
                    ...el.svgs.legend.items[i].dot.config,
                    args: [legendDotRadius, yPos, legendDotRadius],
                };

                // Update label
                const dot = getElementBBox(el.svgs.legend.items[i].dot.svg);
                const text = el.svgs.legend.items[i].label.svg;

                text.attr({
                    x: dot.x + dot.width + 4,
                    y: dot.y + dot.height - 1,
                });

                const textSvg = getElementBBox(text);

                el.svgs.legend.items[i].label.config = {
                    ...el.svgs.legend.items[i].label.config,
                    args: [element.label, textSvg.x, textSvg.y],
                };
            }
        }
    });
};

export const createCustomLegendChildren = (
    el: convertedDataBB,
    status: ChartStatus,
    renderer: Highcharts.SVGRenderer,
    items: convertedDataBB["data"],
    parentIdx: number,
    parentConfig: legendItem,
    hideShowSerie: (element: convertedDataBB["data"][number]) => void
) => {
    items.forEach((element, i) => {
        const isVisible = element.visible;

        if (status.isFirstRender) {
            el.svgs.legend.items[parentIdx].subItems?.push({
                id: 0,
                dot: {} as svgConfigAndExport,
                label: {} as svgConfigAndExport,
            });
        }

        const svgSubitems = el.svgs.legend.items[parentIdx].subItems;
        if (!svgSubitems) return;

        const parentItemDot = getElementBBox(parentConfig.dot.svg);
        const parentItemCircle = getElementBBox(parentConfig.circle.svg);

        // Position for the 1st item
        let xPos = parentItemDot.x;
        let yPos = parentItemCircle.y + parentItemCircle.height + 16;

        // Position for the rest of the items
        const prevElement = el.svgs.legend.items[parentIdx].subItems?.[i - 1];

        if (prevElement) {
            const prevElementConfig = el.svgs.legend.items[parentIdx].subItems?.[i - 1] as {
                dot: svgConfigAndExport;
                label: svgConfigAndExport;
            };
            const dot = getElementBBox(prevElementConfig.dot.svg);
            const label = getElementBBox(prevElementConfig.label.svg);

            xPos = label.x + label.width + 16;
            yPos = dot.y;
        }

        // -----------
        // ID
        // -----------
        svgSubitems[i].id = element.id;

        // -----------
        // Dot
        // -----------
        const dotConfig = {
            ...svgSubitems[i]?.dot.config,
            args: [xPos, yPos, legendDotRadius],
            css: {
                fill: isVisible ? element.color : disabledColor,
                cursor: "pointer" as const,
            },
            attr: {
                zIndex: 20,
            },
        };

        if (status.isFirstRender) {
            const dotSvg = renderer
                .circle(...(dotConfig.args as unknown as Highcharts.SVGAttributes[]))
                .css(dotConfig.css)
                .attr(dotConfig.attr)
                .translate(...(dotConfig?.translate || [0, 0]))
                .on("click", () => hideShowSerie(element))
                .add(parentConfig.group?.svg);

            svgSubitems[i].dot = {
                config: dotConfig,
                svg: dotSvg,
                svgExport: dotSvg,
            };
        }

        if (status.isRedrawing) {
            svgSubitems[i].dot.svg.attr(dotConfig.attr).css(dotConfig.css);
            svgSubitems[i].dot.config = dotConfig;
        }

        if (status.isExporting) {
            const dotSvg = getElementBBox(svgSubitems[i].dot.svg);
            const dotConfig = svgSubitems[i].dot.config;
            renderer
                .circle(...[dotSvg.x, dotSvg.y, legendDotRadius])
                .css(dotConfig.css)
                .attr(dotConfig.attr)
                .translate(dotSvg.e, dotSvg.f)
                .add(parentConfig.group?.svgExport);
        }

        // -----------
        // Label
        // -----------
        const dot = getElementBBox(svgSubitems[i]?.dot?.svg);
        const x = dot.x + dot.width * 2;
        const y = dot.y + dot.height / 2 + 1;

        const labelConfig = {
            ...svgSubitems[i]?.label.config,
            args: [element.label, x, y],
            css: {
                ...labelDefaultCSS,
                fontSize: "12px",
                fontWeight: "semibold",
                color: isVisible ? "#202020" : disabledColor,
                cursor: "pointer" as const,
            },
        };

        if (status.isFirstRender) {
            const labelSvg = renderer
                // @ts-expect-error expected error
                .text(...labelConfig.args)
                .css(labelConfig.css as Highcharts.CSSObject)
                .translate(...(svgSubitems[i]?.label.config?.translate || [0, 0]))
                .on("click", () => hideShowSerie(element))
                .add(parentConfig.group?.svg);

            svgSubitems[i].label = {
                config: labelConfig,
                svg: labelSvg,
                svgExport: labelSvg,
            };
        }

        if (status.isRedrawing) {
            svgSubitems[i].label.svg.attr({ x, y }).css(labelConfig.css);
            svgSubitems[i].label.config = labelConfig;
        }

        if (status.isExporting) {
            const labelSvg = getElementBBox(svgSubitems[i].label.svg);

            renderer
                // @ts-expect-error expected error
                .text(...labelConfig.args)
                .css(labelConfig.css as Highcharts.CSSObject)
                .translate(labelSvg.e, labelSvg.f)
                .add(parentConfig.group?.svgExport);
        }

        const label = getElementBBox(svgSubitems[i].label.svg);

        // Validate new line of items
        if (label.x + label.width > parentItemCircle.x + parentItemCircle.width) {
            // Update dot
            const prevElement = svgSubitems[i - 1];
            const prevDot = getElementBBox(prevElement.dot.svg);
            const prevLabel = getElementBBox(prevElement.label.svg);

            svgSubitems[i].dot.svg.attr({
                x: parentItemDot.x,
                y: prevDot.y + prevLabel.height + 8,
            });

            const currentDot = getElementBBox(svgSubitems[i].dot.svg);

            svgSubitems[i].dot.config = {
                ...svgSubitems[i].dot.config,
                args: [currentDot.x, currentDot.y, currentDot.width],
            };

            // Update label
            svgSubitems[i].label.svg.attr({
                x: currentDot.x + currentDot.width * 2,
                y: currentDot.y + currentDot.height / 2 + 1,
            });

            const currentLabel = getElementBBox(svgSubitems[i].label.svg);

            svgSubitems[i].label.config = {
                ...svgSubitems[i].label.config,
                args: [element.label, currentLabel.x, currentLabel.y],
            };
        }
    });
};
